import { ConnectedWorkflowArt } from '../components/ConnectedWorkflowArt';
import { ArrowIcon, MarketingHeader, MarketingFooter } from '../components/MarketingChrome';
import { appPath } from '../lib/config';
import { pluginLaunch } from '../data/pluginLaunch';
import '../components/marketing-home.css';
import './chatgpt-plugin.css';

export function LandingPage() {
  return (
    <div className="marketing-home">
      <a className="mh-skip" href="#main">
        Skip to content
      </a>
      <MarketingHeader />
      <main id="main">
        <section className="enterprise-hero">
          <div className="mh-shell enterprise-hero__grid">
            <div className="enterprise-hero__copy">
              <p className="enterprise-hero__eyebrow">
                <span aria-hidden="true" /> Race analysis for drivers and teams
              </p>
              <h1>
                See where you
                <br />
                <em>gain and lose time.</em>
              </h1>
              <p className="enterprise-hero__lead">
                Upload your MyChron data, compare laps, and review each corner with your coach. Keep
                your sessions and kart setups ready for the next race.
              </p>
              <div className="enterprise-hero__actions">
                <a className="mh-button" href={appPath('/register')}>
                  Get started with Paddiq <ArrowIcon />
                </a>
                <a className="enterprise-hero__secondary" href="/paddiq-link">
                  Paddiq Link · Beta <ArrowIcon />
                </a>
              </div>
            </div>
            <figure className="enterprise-hero__art">
              <img
                src="/hero-enterprise-team.png"
                alt="Illustration of a driver and engineer discussing race data together"
                width="1536"
                height="1024"
                fetchPriority="high"
              />
            </figure>
          </div>
        </section>
        <section className="mh-product-strip" aria-label="Meet the products">
          <a href="#product">
            <span className="mh-index">01 / ANALYZE</span>
            <div>
              <h2>Paddiq</h2>
              <p>Compare laps and review your race data.</p>
            </div>
            <ArrowIcon />
          </a>
          <a href="/paddiq-link">
            <span className="mh-index">02 / BETA</span>
            <div>
              <h2>Paddiq Link Beta</h2>
              <p>Transfer MyChron sessions to Paddiq.</p>
            </div>
            <ArrowIcon />
          </a>
        </section>
        <section className="plugin-announcement" aria-labelledby="plugin-announcement-title">
          <div className="mh-shell plugin-announcement__inner">
            <div>
              <p className="mh-kicker">{pluginLaunch.status}</p>
              <h2 id="plugin-announcement-title">Your Paddiq sessions, in ChatGPT.</h2>
              <p>{pluginLaunch.timing}</p>
              <p className="plugin-updated">
                Update: <time dateTime={pluginLaunch.updatedAt}>{pluginLaunch.updatedLabel}</time>
              </p>
            </div>
            <a className="mh-text-link mh-text-link--purple" href="/chatgpt">
              Explore the upcoming plugin <ArrowIcon />
            </a>
          </div>
        </section>
        <section className="mh-section" id="product">
          <div className="mh-shell">
            <div className="mh-section-heading">
              <div>
                <p className="mh-kicker">Paddiq / Session analysis</p>
                <h2>
                  Keep your MyChron.
                  <br />
                  Get more from its data.
                </h2>
              </div>
              <p>
                Review laps alongside the driver, kart, circuit, and setup used for each run.
                Compare sessions to see what changed.
              </p>
            </div>
            <div className="mh-analysis">
              <figure className="mh-analysis__image">
                <img
                  src="/illustration-session-overview.png"
                  alt="Illustration connecting a race circuit and compared driving lines with telemetry, session history, and the team"
                  loading="lazy"
                  width="1536"
                  height="1024"
                />
                <figcaption>LAP COMPARISON AND SESSION HISTORY</figcaption>
              </figure>
              <div className="mh-analysis__copy">
                <article>
                  <span className="mh-index">01</span>
                  <h3>Compare corner speeds.</h3>
                  <p>
                    Compare laps, follow synchronized traces, and see the difference in entry, apex,
                    and exit speed.
                  </p>
                </article>
                <article>
                  <span className="mh-index">02</span>
                  <h3>Keep track of each run.</h3>
                  <p>
                    Record the driver, kart, circuit, and setup for each session so you can find the
                    runs you want to compare.
                  </p>
                </article>
                <article>
                  <span className="mh-index">03</span>
                  <h3>Review sessions with your team.</h3>
                  <p>
                    Give drivers, coaches, and team managers access to the same sessions. Review a
                    lap together before deciding what to work on next.
                  </p>
                </article>
                <a className="mh-text-link mh-text-link--purple" href={appPath('/register')}>
                  Create your account <ArrowIcon />
                </a>
              </div>
            </div>
          </div>
        </section>
        <section className="mh-link-section" id="paddiq-link">
          <div className="mh-shell mh-link-layout">
            <div>
              <p className="mh-kicker">Paddiq Link / Beta</p>
              <h2>
                Transfer sessions
                <br />
                with Paddiq Link.
              </h2>
              <p>
                Paddiq Link is our beta desktop app for transferring MyChron sessions over paddock
                Wi-Fi. Choose the driver, kart, and circuit, then review the uploaded runs in
                Paddiq.
              </p>
              <a className="mh-button" href="/paddiq-link">
                Paddiq Link Beta downloads <ArrowIcon />
              </a>
              <p>
                <a className="mh-text-link mh-text-link--purple" href="/resources/paddiq-link">
                  Read the Paddiq Link guide <ArrowIcon />
                </a>
              </p>
            </div>
            <ConnectedWorkflowArt />
          </div>
        </section>
        <section className="mh-section mh-workflow" id="how-it-works">
          <div className="mh-shell">
            <p className="mh-kicker">Analysis tools</p>
            <h2>Take a closer look at your laps.</h2>
            <div className="mh-three-columns mh-feature-gallery">
              <article>
                <div className="mh-feature-gallery__image">
                  <img
                    src="/illustration-lap-comparison.png"
                    alt="Illustration of two racing lines compared under a magnifying lens"
                    loading="lazy"
                    width="1536"
                    height="1024"
                  />
                </div>
                <span className="mh-index">01 / LAP COMPARISON</span>
                <h3>Compare laps across sessions.</h3>
                <p>
                  Add comparison laps from the same session or other runs. Compare driving lines and
                  speed around the circuit.
                </p>
                <a
                  className="mh-text-link mh-text-link--purple"
                  href="/resources/compare-laps-and-traces"
                >
                  How to compare laps <ArrowIcon />
                </a>
              </article>
              <article>
                <div className="mh-feature-gallery__image">
                  <img
                    src="/illustration-corner-analysis.png"
                    alt="Illustration of a kart taking a corner with entry, apex, and exit markers"
                    loading="lazy"
                    width="1536"
                    height="1024"
                  />
                </div>
                <span className="mh-index">02 / CORNER ANALYSIS</span>
                <h3>Check entry, apex, and exit.</h3>
                <p>
                  Compare speeds through each turn, then check the map and traces to see where the
                  laps differ.
                </p>
                <a
                  className="mh-text-link mh-text-link--purple"
                  href="/resources/compare-laps-and-traces#turns"
                >
                  How to review corners <ArrowIcon />
                </a>
              </article>
              <article>
                <div className="mh-feature-gallery__image">
                  <img
                    src="/illustration-telemetry.png"
                    alt="Illustration of three compared telemetry traces aligned to the same point"
                    loading="lazy"
                    width="1536"
                    height="1024"
                  />
                </div>
                <span className="mh-index">03 / TELEMETRY TRACES</span>
                <h3>Choose the traces you need.</h3>
                <p>
                  Review speed, RPM, and other recorded channels side by side. Replay a section to
                  follow the differences between laps.
                </p>
                <a
                  className="mh-text-link mh-text-link--purple"
                  href="/resources/compare-laps-and-traces"
                >
                  How to read traces <ArrowIcon />
                </a>
              </article>
            </div>
          </div>
        </section>
        <section className="mh-compatibility">
          <div className="mh-shell">
            <div>
              <p className="mh-kicker">Supported hardware</p>
              <h2>Use your existing MyChron files.</h2>
            </div>
            <div>
              <h3>AiM MyChron 5 &amp; 6 session files</h3>
              <p>
                Import XRZ, XRK, HRZ, HRK, DRK, or MyChron JSON files. See Paddiq Link Beta for
                desktop transfer capabilities and available downloads.
              </p>
              <a
                className="mh-text-link mh-text-link--purple"
                href="/resources/upload-mychron-session"
              >
                See import options <ArrowIcon />
              </a>
            </div>
          </div>
        </section>
        <section className="mh-story mh-team-story" id="why-paddiq">
          <div className="mh-shell mh-team-story__inner">
            <div>
              <p className="mh-kicker">Team management</p>
              <h2>Keep your team’s sessions organized.</h2>
              <p>
                Manage drivers and karts alongside your session history. Keep setup records so you
                can check what the team ran at the last event.
              </p>
              <a className="mh-text-link mh-text-link--purple" href="/resources/add-team-members">
                Set up your team <ArrowIcon />
              </a>
            </div>
            <img
              src="/illustration-team-debrief.png"
              alt="Illustration of a driver, engineer, and coach reviewing circuit and telemetry data together in the paddock"
              loading="lazy"
              width="1536"
              height="1024"
            />
          </div>
        </section>
        <section className="mh-section mh-pricing" id="pricing">
          <div className="mh-shell mh-pricing__inner">
            <div>
              <p className="mh-kicker">Try Paddiq</p>
              <h2>
                Your first five uploads
                <br />
                are free.
              </h2>
              <p>
                Create your Paddiq account and upload five sessions at no charge. Your existing
                sessions stay available to review.
              </p>
              <a className="mh-button" href={appPath('/register')}>
                Create your account <ArrowIcon />
              </a>
            </div>
            <div className="mh-price-card">
              <span className="mh-index">AFTER YOUR FIRST 5 FREE UPLOADS</span>
              <p className="mh-price">
                <strong>$9</strong> / active billing period
              </p>
              <p>
                A monthly billing period is active when you upload a billable session. The $9
                includes 5 billable session uploads.
              </p>
              <ul>
                <li>$1 per additional billable session in that billing period</li>
                <li>No session usage charge in periods with no billable session uploads</li>
                <li>Billing managed inside Paddiq</li>
              </ul>
              <a className="mh-text-link mh-text-link--purple" href="/resources/session-billing">
                How session billing works <ArrowIcon />
              </a>
            </div>
          </div>
        </section>
        <section className="mh-help">
          <div className="mh-shell">
            <h2>Getting started with Paddiq</h2>
            <a href="/resources/getting-started">
              Read the getting started guide <ArrowIcon />
            </a>
            <a
              href="https://apps.apple.com/sa/app/paddiq/id6785660120"
              target="_blank"
              rel="noreferrer"
            >
              Get Paddiq for iPhone <ArrowIcon />
            </a>
          </div>
        </section>
      </main>
      <MarketingFooter />
    </div>
  );
}
